import { ButtonVariant, ButtonSize } from '@homeflow/v4-lib/state';
import { ButtonProps } from '@homeflow/v4-lib/theme/components/buttons/button-props';
import { CSSProperties, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import variantClassNames from '../../button-variants';

const DEFAULT_VARIANT = ButtonVariant.A;
export const DEFAULT_SIZE = ButtonSize.Medium;

export const sizeClassNames: { [key in ButtonSize]?: string } = {
  [ButtonSize.Small]: 'px-4 py-1',
  [ButtonSize.Medium]: 'px-8 py-2',
  [ButtonSize.Large]: 'px-16 py-4',
};

const variantClassName = (variant: ButtonVariant = DEFAULT_VARIANT, noDefaultClasses?: boolean) => {
  const className = variantClassNames[variant];
  if (className) return className;
  if (noDefaultClasses) return null;
  return variantClassNames[DEFAULT_VARIANT];
};

const buttonSizeClasses = (buttonSize: ButtonSize = DEFAULT_SIZE) => {
  return sizeClassNames[buttonSize] || sizeClassNames[DEFAULT_SIZE];
};

export const buttonClasses = (
  variant?: ButtonVariant,
  size?: ButtonSize,
  noDefaultClasses?: boolean
) => {
  const calculatedVariantClassName = variantClassName(variant, noDefaultClasses);
  if (!calculatedVariantClassName) return null;
  return twMerge(calculatedVariantClassName, buttonSizeClasses(size));
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant, size, noDefaultClasses, className, style, children, ...otherProps }: ButtonProps,
    ref
  ) => {
    const buttonClassName = buttonClasses(variant, size, noDefaultClasses);
    if (!buttonClassName) return null;
    return (
      <button
        className={twMerge(buttonClassName, className)}
        ref={ref}
        style={style as CSSProperties}
        {...otherProps}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
